import { useState } from 'react';
import { api, type User } from '../lib/api.ts';
import { useAuth } from '../App.tsx';

/**
 * Ekran logowania na halę: login pracownika + PIN na klawiaturze numerycznej.
 * Duże cele dotykowe, bo wchodzi się w rękawicy, często na wspólnym tablecie.
 */
export default function Login() {
  const { setUser } = useAuth();
  const [login, setLogin] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!login.trim() || pin.length < 4) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ user: User }>('/auth/login', { login: login.trim(), pin });
      setUser(r.user);
    } catch (e: any) {
      setError(e.message ?? 'Nie udało się zalogować');
      setPin('');
    } finally {
      setBusy(false);
    }
  };

  const key = (d: string) => setPin((p) => (p.length >= 8 ? p : p + d));

  return (
    <div className="min-h-dvh grid place-items-center px-4 py-8 bg-canvas">
      <div className="w-full max-w-sm">
        <div className="mb-7 text-center">
          <h1 className="text-head text-plum">Efektywność A4</h1>
          <p className="text-body-sm text-plum-muted mt-1">Rejestracja pracy produkcyjnej</p>
        </div>

        <div className="card p-5">
          <label className="label" htmlFor="login">Twój login</label>
          <input
            id="login"
            className="field field-lg uppercase tracking-wide font-semibold tnum"
            autoCapitalize="characters"
            autoComplete="username"
            placeholder="np. CWJ"
            value={login}
            onChange={(e) => setLogin(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }}
          />

          <div className="mt-4">
            <span className="label">PIN</span>
            <div className="flex gap-2 justify-center py-2" aria-live="polite">
              {Array.from({ length: 6 }).map((_, i) => (
                <span
                  key={i}
                  className={`w-3.5 h-3.5 rounded-pill border transition-colors ${
                    i < pin.length ? 'bg-plum border-plum' : 'border-line-strong bg-canvas'
                  }`}
                />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 mt-3">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
              <button key={d} className="btn-ghost !min-h-[58px] text-sub font-semibold tnum" onClick={() => key(d)}>
                {d}
              </button>
            ))}
            <button className="btn-ghost !min-h-[58px] text-body-sm" onClick={() => setPin('')}>
              Wyczyść
            </button>
            <button className="btn-ghost !min-h-[58px] text-sub font-semibold tnum" onClick={() => key('0')}>
              0
            </button>
            <button className="btn-ghost !min-h-[58px] text-sub" onClick={() => setPin((p) => p.slice(0, -1))} aria-label="Skasuj cyfrę">
              ⌫
            </button>
          </div>

          {error && (
            <p className="mt-4 text-body-sm text-bad bg-bad/[0.06] border border-bad/25 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <button
            className="btn-primary btn-lg w-full mt-4"
            disabled={busy || !login.trim() || pin.length < 4}
            onClick={submit}
          >
            {busy ? 'Logowanie…' : 'Zaloguj'}
          </button>
        </div>

        <p className="text-caption text-plum-muted text-center mt-4">
          Nie znasz PIN-u? Zgłoś się do brygadzisty.
        </p>
      </div>
    </div>
  );
}
